import {
  AlertCircle,
  Check,
  CheckCircle2,
  CloudOff,
  ExternalLink,
  FolderSync,
  Loader2,
  Pencil,
  Unplug,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDisconnectDropboxMutation,
  useGetDropboxStatusQuery,
  useUpdateDropboxRootPathMutation,
} from '@/services/api/dropboxService';

const BACKEND_HOST =
  import.meta.env.VITE_APP_DJANGO_BACKEND_HOST || 'http://localhost:8000';

interface DropboxIntegrationProps {
  firmId: string;
}

const DropboxIntegrationSkeleton: React.FC = () => (
  <div className="border rounded-lg bg-white overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-44" />
        </div>
      </div>
      <Skeleton className="h-6 w-24 rounded-full" />
    </div>
  </div>
);

const DropboxLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 43 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.5 0L0 8.1L12.5 16.2L25 8.1L12.5 0Z"
      fill="#0061FF"
    />
    <path
      d="M0 24.4L12.5 32.5L25 24.4L12.5 16.3L0 24.4Z"
      fill="#0061FF"
    />
    <path
      d="M25 24.4L37.5 32.5L50 24.4L37.5 16.3L25 24.4Z"
      fill="#0061FF"
    />
    <path
      d="M50 8.1L37.5 0L25 8.1L37.5 16.2L50 8.1Z"
      fill="#0061FF"
    />
    <path
      d="M12.55 34.6L25 26.5L37.45 34.6L25 42.7L12.55 34.6Z"
      fill="#0061FF"
    />
  </svg>
);

export const DropboxIntegration: React.FC<DropboxIntegrationProps> = ({
  firmId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [connectRootPath, setConnectRootPath] = useState('/Finch');
  const [isEditingRootPath, setIsEditingRootPath] = useState(false);
  const [editRootPath, setEditRootPath] = useState('');

  const {
    data: status,
    isLoading,
    refetch,
  } = useGetDropboxStatusQuery(
    { firmId },
    { refetchOnMountOrArgChange: true },
  );

  const [disconnectDropbox, { isLoading: isDisconnecting }] =
    useDisconnectDropboxMutation();

  const [updateRootPath, { isLoading: isUpdatingRootPath }] =
    useUpdateDropboxRootPathMutation();

  // Show success toast when redirected back from OAuth
  useEffect(() => {
    if (searchParams.get('dropbox') === 'connected') {
      toast.success('Dropbox connected successfully');
      searchParams.delete('dropbox');
      setSearchParams(searchParams, { replace: true });
      refetch();
    }
  }, [searchParams, setSearchParams, refetch]);

  const handleConnect = () => {
    const rootParam = connectRootPath.trim()
      ? `&root_path=${encodeURIComponent(connectRootPath.trim())}`
      : '';
    window.location.href = `${BACKEND_HOST}/api/integrations/dropbox/connect?firm_id=${firmId}${rootParam}`;
  };

  const handleSaveRootPath = async () => {
    try {
      await updateRootPath({
        firmId,
        rootPath: editRootPath.trim(),
      }).unwrap();
      toast.success('Sync folder updated');
      setIsEditingRootPath(false);
    } catch {
      toast.error('Failed to update sync folder');
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectDropbox({ firmId }).unwrap();
      toast.success('Dropbox disconnected');
      setIsDisconnectDialogOpen(false);
    } catch {
      toast.error('Failed to disconnect Dropbox');
    }
  };

  if (isLoading) {
    return <DropboxIntegrationSkeleton />;
  }

  const isConnected = status?.connected;

  return (
    <>
      <div className="border rounded-lg bg-white overflow-hidden">
        {/* Card header: logo, name, description, status */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 border border-blue-100">
              <DropboxLogo className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  Dropbox
                </h3>
                <span className="text-xs text-gray-500">
                  Cloud file storage
                </span>
              </div>
              {isConnected ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  {status.display_name || 'Dropbox User'}
                  {status.email ? ` \u00B7 ${status.email}` : ''}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-0.5">
                  Sync documents between Finch and Dropbox
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isConnected ? (
              <>
                <Badge
                  variant="default"
                  className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 font-medium"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDisconnectDialogOpen(true)}
                  className="text-gray-500 hover:text-red-600 h-8 px-2"
                >
                  <Unplug className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button
                onClick={handleConnect}
                size="sm"
                className="h-8"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Connect
              </Button>
            )}
          </div>
        </div>

        {/* Connected: sync status + configuration */}
        {isConnected && (
          <div className="border-t bg-blue-50/50 px-5 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-blue-700">
                <FolderSync className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                <span className="font-medium">
                  Document sync is active.
                </span>
                <span className="text-blue-600">
                  Configure push rules in the Document Sync tab.
                </span>
              </div>

              {/* Sync folder path */}
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500">Folder:</span>
                {isEditingRootPath ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={editRootPath}
                      onChange={(e) => setEditRootPath(e.target.value)}
                      placeholder="/Finch"
                      className="h-6 text-xs font-mono w-32 px-1.5"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={handleSaveRootPath}
                      disabled={isUpdatingRootPath}
                    >
                      {isUpdatingRootPath ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Check className="h-3 w-3 text-green-600" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-1 text-xs text-gray-500"
                      onClick={() => setIsEditingRootPath(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setEditRootPath(status.root_path || '');
                      setIsEditingRootPath(true);
                    }}
                    className="flex items-center gap-1 text-gray-700 hover:text-gray-900 font-mono"
                  >
                    {status.root_path || '/'}
                    <Pencil className="h-2.5 w-2.5 text-gray-500" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Disconnected: empty state with root path config */}
        {!isConnected && (
          <div className="border-t bg-gray-50/50 px-5 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <CloudOff className="h-3.5 w-3.5 shrink-0" />
                <span>
                  No account connected. Click Connect to authorize via
                  Dropbox OAuth.
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500">Root folder:</span>
                <Input
                  value={connectRootPath}
                  onChange={(e) => setConnectRootPath(e.target.value)}
                  placeholder="/Finch"
                  className="h-6 text-xs font-mono w-24 px-1.5"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Disconnect confirmation dialog */}
      <Dialog
        open={isDisconnectDialogOpen}
        onOpenChange={setIsDisconnectDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Dropbox</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect this Dropbox account?
              Document sync will stop, but existing synced documents will
              remain in both Finch and Dropbox.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertCircle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
            <p>
              Any in-progress sync operations will be cancelled. You can
              reconnect at any time.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDisconnectDialogOpen(false)}
              disabled={isDisconnecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
